import { AuthClientInstance, getAuthClient } from "@trumpf/ux.utils";
import { i18nProvider } from "@trumpf/ux.i18n";
import { AuthenticationError } from "../backend/chatService";

export interface IAuthState {
    token: string | null;
    isAuthenticated: boolean;
    error: string | null;
}

interface IStoredToken {
    token: string;
    expiration: number | null;
    timestamp: number;
}

type AuthSubscriber = (state: IAuthState) => void;

// Storage keys
const TOKEN_STORAGE_KEY = "trumpf_auth_token";

class AuthService {
    private static instance: AuthService;
    private authClient: AuthClientInstance;
    private subscribers: Set<AuthSubscriber> = new Set();
    private state: IAuthState = {
        token: null,
        isAuthenticated: false,
        error: null
    };
    private tokenRefreshInterval: NodeJS.Timeout | null = null;

    private constructor() {
        this.authClient = getAuthClient();
        this.loadPersistedToken();
        this.setupTokenRefresh();
    }

    public static getInstance(): AuthService {
        if (!AuthService.instance) {
            AuthService.instance = new AuthService();
        }
        return AuthService.instance;
    }

    private notifySubscribers() {
        this.subscribers.forEach((subscriber) => subscriber({ ...this.state }));
    }

    public subscribe(callback: AuthSubscriber): () => void {
        this.subscribers.add(callback);
        callback({ ...this.state });
        return () => {
            this.subscribers.delete(callback);
        };
    }

    private setupTokenRefresh() {
        // Check token expiration every minute
        this.tokenRefreshInterval = setInterval(() => {
            const expiration = this.authClient.getTokenExpiration();
            if (expiration) {
                const now = Math.floor(Date.now() / 1000);
                // Refresh token if it expires in less than 5 minutes
                if (expiration - now < 300) {
                    void this.refreshToken();
                }
            } else {
                // If no expiration found, check our stored token
                this.checkPersistedTokenExpiration();
            }
        }, 60000);
    }

    private persistToken(token: string): void {
        try {
            const expiration = this.authClient.getTokenExpiration();
            const tokenData: IStoredToken = {
                token,
                expiration: expiration || null,
                timestamp: Date.now()
            };
            localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
        } catch (error) {
            console.error("[AuthService] Failed to persist token:", error);
        }
    }

    private loadPersistedToken(): void {
        try {
            const storedData = localStorage.getItem(TOKEN_STORAGE_KEY);
            if (!storedData) {
                return;
            }

            const tokenData: IStoredToken = JSON.parse(storedData);

            // Check if token is expired
            if (tokenData.expiration) {
                const now = Math.floor(Date.now() / 1000);
                if (tokenData.expiration <= now) {
                    localStorage.removeItem(TOKEN_STORAGE_KEY);
                    return;
                }
            } else {
                // If no expiration info, check if token is older than 1 hour
                const oneHour = 60 * 60 * 1000;
                if (Date.now() - tokenData.timestamp > oneHour) {
                    localStorage.removeItem(TOKEN_STORAGE_KEY);
                    return;
                }
            }

            // Valid token found, set it to our state
            this.state = {
                token: tokenData.token,
                isAuthenticated: true,
                error: null
            };

            // Also set it in the authClient for consistency
            this.authClient.setAccessToken(tokenData.token);

            // Notify subscribers of the loaded token
            this.notifySubscribers();
        } catch (error) {
            console.error("[AuthService] Failed to load persisted token:", error);
            localStorage.removeItem(TOKEN_STORAGE_KEY);
        }
    }

    private checkPersistedTokenExpiration(): void {
        try {
            const storedData = localStorage.getItem(TOKEN_STORAGE_KEY);
            if (!storedData) {
                return;
            }

            const tokenData: IStoredToken = JSON.parse(storedData);
            const now = Math.floor(Date.now() / 1000);

            // Check if token has expiration and is expired
            if (tokenData.expiration && tokenData.expiration <= now) {
                localStorage.removeItem(TOKEN_STORAGE_KEY);

                // Clear token from state if it matches the expired one
                if (this.state.token === tokenData.token) {
                    this.state = {
                        token: null,
                        isAuthenticated: false,
                        error: null
                    };
                    this.notifySubscribers();
                }
            }
        } catch (error) {
            console.error("[AuthService] Error checking persisted token:", error);
        }
    }

    public async getToken(maxRetries = 10, delayMs = 200): Promise<string> {
        try {
            // Try to get cached token first
            if (this.state.token) {
                return this.state.token;
            }

            // Try to get a new token
            let token = this.authClient.getIdToken();
            if (!token) {
                token = this.authClient.getAccessToken();
            }

            if (!token) {
                token = await this.waitForToken(maxRetries, delayMs);
            }

            if (!token) {
                throw new AuthenticationError(i18nProvider.t("ai-in-service:error.failedToRetrieveToken"));
            }

            this.state = {
                token,
                isAuthenticated: true,
                error: null
            };

            // Persist the token
            this.persistToken(token);

            this.notifySubscribers();

            return token;
        } catch (error) {
            const authError =
                error instanceof AuthenticationError
                    ? error
                    : new AuthenticationError(i18nProvider.t("ai-in-service:error.authenticationFailed"), error);

            this.state = {
                token: null,
                isAuthenticated: false,
                error: authError.message
            };
            this.notifySubscribers();
            throw authError;
        }
    }

    private async waitForToken(maxRetries: number, delayMs: number): Promise<string | null> {
        for (let attempt = 0; attempt < maxRetries; attempt++) {
            const idToken = this.authClient.getIdToken();
            if (idToken) {
                return idToken;
            }

            const accessToken = this.authClient.getAccessToken();
            if (accessToken) {
                return accessToken;
            }

            await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
        return null;
    }

    private async refreshToken(): Promise<void> {
        try {
            // Attempt to get a fresh token
            const token = await this.getToken();
            this.state = {
                token,
                isAuthenticated: true,
                error: null
            };
            // Token will be persisted by getToken()
            this.notifySubscribers();
        } catch (error) {
            console.error("Failed to refresh token:", error);
            // Don't update state here as getToken() already handles error states
        }
    }

    public clearToken(): void {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        this.state = {
            token: null,
            isAuthenticated: false,
            error: null
        };
        this.notifySubscribers();
    }

    public destroy() {
        if (this.tokenRefreshInterval) {
            clearInterval(this.tokenRefreshInterval);
            this.tokenRefreshInterval = null;
        }
        this.subscribers.clear();
    }
}

export const authService = AuthService.getInstance();
